<template>
  <div class="dashboard-container">
    <!-- 欢迎横幅 -->
    <div class="welcome-banner">
      <div class="banner-content">
        <div class="welcome-text">
          <h1>欢迎回来，{{ userInfo.account || '用户' }}！</h1>
          <p>AI内容创作平台 · 让创作更简单</p>
        </div>
        <div class="banner-stats">
          <div class="banner-stat-item">
            <Icon icon="heroicons:sparkles" width="24" height="24" />
            <div>
              <span class="stat-num">{{ totalPoints }}</span>
              <span class="stat-label">可用点数</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 数据卡片区域 -->
    <div class="stats-grid">
      <div class="stat-card" v-for="(item, index) in stats" :key="index">
        <div class="card-icon" :style="{ background: getGradientColor(index) }">
          <Icon :icon="item.icon" width="28" height="28" />
        </div>
        <div class="card-info">
          <div class="card-value">
            <el-skeleton v-if="item.loading" :rows="1" animated style="width: 60px;" />
            <span v-else>{{ item.value }}</span>
          </div>
          <div class="card-label">{{ item.label }}</div>
        </div>
      </div>
    </div>

    <!-- 快捷功能入口 -->
    <div class="quick-actions">
      <h2 class="section-title">
        <Icon icon="heroicons:rocket-launch" width="24" height="24" />
        快捷功能
      </h2>
      <div class="actions-grid">
        <div class="action-card" v-for="action in quickActions" :key="action.name" @click="navigateTo(action.path)">
          <div class="action-icon" :style="{ background: action.gradient }">
            <Icon :icon="action.icon" width="24" height="24" />
          </div>
          <span class="action-name">{{ action.name }}</span>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="chart-section">
      <h2 class="section-title">
        <Icon icon="heroicons:chart-bar" width="24" height="24" />
        数据统计
      </h2>
      <div class="chart-container">
        <div class="chart-card">
          <div class="chart-header">
            <Icon icon="heroicons:video-camera" width="20" height="20" class="chart-icon generate" />
            <span>生成视频趋势</span>
          </div>
          <div id="generateChart" style="height: 320px;"></div>
        </div>

        <div class="chart-card">
          <div class="chart-header">
            <Icon icon="heroicons:share" width="20" height="20" class="chart-icon publish" />
            <span>发布视频趋势</span>
          </div>
          <div id="publishChart" style="height: 320px;"></div>
        </div>
      </div>
    </div>

    <!-- 热点数据区域 -->
    <div class="hotspot-section">
      <h2 class="section-title">
        <Icon icon="heroicons:fire" width="24" height="24" />
        热点榜单
      </h2>
      <div class="hotspot-grid">
        <!-- 抖音热点 -->
        <div class="hotspot-card">
          <div class="hotspot-header" style="background: linear-gradient(135deg, #fe2c55, #ff6b6b);">
            <Icon icon="ri:tiktok-fill" width="20" height="20" />
            <span>抖音热榜</span>
          </div>
          <div class="hotspot-list">
            <div v-for="(item, index) in douyinData.slice(0, 8)" :key="item.id || index" class="hotspot-item"
              @click="openLink(item.url)">
              <span class="hotspot-rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="hotspot-text">{{ item.title }}</span>
              <span class="hotspot-hot">{{ formatHot(item.hot) }}</span>
            </div>
            <div v-if="douyinData.length === 0" class="empty-data">暂无数据</div>
          </div>
        </div>

        <!-- 微博热点 -->
        <div class="hotspot-card">
          <div class="hotspot-header" style="background: linear-gradient(135deg, #e71f19, #ff4d4d);">
            <Icon icon="ri:weibo-fill" width="20" height="20" />
            <span>微博热搜</span>
          </div>
          <div class="hotspot-list">
            <div v-for="(item, index) in weiboData.slice(0, 8)" :key="item.id || index" class="hotspot-item"
              @click="openLink(item.url)">
              <span class="hotspot-rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="hotspot-text">{{ item.title }}</span>
              <span class="hotspot-hot">{{ formatHot(item.hot) }}</span>
            </div>
            <div v-if="weiboData.length === 0" class="empty-data">暂无数据</div>
          </div>
        </div>

        <!-- 腾讯新闻 -->
        <div class="hotspot-card">
          <div class="hotspot-header" style="background: linear-gradient(135deg, #0084ff, #4da6ff);">
            <Icon icon="ri:news-line" width="20" height="20" />
            <span>腾讯新闻</span>
          </div>
          <div class="hotspot-list">
            <div v-for="(item, index) in zhihuData.slice(0, 8)" :key="item.id || index" class="hotspot-item"
              @click="openLink(item.url)">
              <span class="hotspot-rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="hotspot-text">{{ item.title }}</span>
              <span class="hotspot-author">{{ item.author || '热点' }}</span>
            </div>
            <div v-if="zhihuData.length === 0" class="empty-data">暂无数据</div>
          </div>
        </div>

        <!-- 百度热点 -->
        <div class="hotspot-card">
          <div class="hotspot-header" style="background: linear-gradient(135deg, #2932e1, #5a63ff);">
            <Icon icon="ri:baidu-fill" width="20" height="20" />
            <span>百度热搜</span>
          </div>
          <div class="hotspot-list">
            <div v-for="(item, index) in baiduData.slice(0, 8)" :key="item.id || index" class="hotspot-item"
              @click="openLink(item.url)">
              <span class="hotspot-rank" :class="getRankClass(index)">{{ index + 1 }}</span>
              <span class="hotspot-text">{{ item.title }}</span>
              <span class="hotspot-hot">{{ formatHot(item.hot) }}</span>
            </div>
            <div v-if="baiduData.length === 0" class="empty-data">暂无数据</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Icon } from '@iconify/vue'

export default {
  name: 'TheDashboard',
  components: { Icon },
  data() {
    return {
      stats: [
        { label: '账号数量', value: 0, loading: true, icon: 'heroicons:users', key: 'total_accounts', action: 1 },
        { label: '视频数量', value: 0, loading: true, icon: 'heroicons:film', key: 'total_articles', action: 2 },
        { label: '总发布视频', value: 0, loading: true, icon: 'heroicons:share', key: 'total_published', action: 3 },
        { label: '今日发布', value: 0, loading: true, icon: 'heroicons:clock', key: 'today_published', action: 3 },
        { label: '标题数量', value: 0, loading: true, icon: 'heroicons:document-text', key: 'total_titles', action: 5 },
        { label: '图库数量', value: 0, loading: true, icon: 'heroicons:photo', key: 'total_images', action: 4 }
      ],
      // 快捷功能 - 固定在 data 中
      quickActions: [
        { name: '数字人克隆', path: '/human_clone/humanclone', icon: 'carbon:user-avatar', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { name: '视频合成', path: '/human_clone/humanrecord', icon: 'heroicons:video-camera', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        { name: '声音克隆', path: '/vocie_clone/VoiceClone', icon: 'heroicons:microphone', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { name: '一键成片', path: '/videocut/video-cut', icon: 'heroicons:sparkles', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
        { name: '视频发布', path: '/video_publish/draft', icon: 'heroicons:share', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
        { name: '图文发布', path: '/tw_publish/draft', icon: 'heroicons:newspaper', gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }
      ],
      douyinData: [],
      weiboData: [],
      zhihuData: [],
      baiduData: [],
      dailyGenerated: [],
      dailyPublished: [],
      totalPoints: 0,
      genChart: null,
      pubChart: null
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.initCharts()
    this.fetchHotData()
    this.fetchDashboardStats()
    this.fetchChartData()
    this.loadPoints()
  },
  beforeUnmount() {
    if (this.genChart) {
      this.genChart.dispose()
    }
    if (this.pubChart) {
      this.pubChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    getGradientColor(index) {
      const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
      ]
      return gradients[index % gradients.length]
    },

    getRankClass(index) {
      if (index === 0) return 'rank-1'
      if (index === 1) return 'rank-2'
      if (index === 2) return 'rank-3'
      return ''
    },

    navigateTo(path) {
      this.$router.push(path)
    },

    async loadPoints() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/shop_api.php`, {
          action: 'query',
          account: this.userInfo.account
        })
        if (response.data.success) {
          const points = response.data.data.dianshu
          this.totalPoints = points === -1 ? '∞' : parseFloat(points || 0).toFixed(1)
        }
      } catch (error) {
        console.error('加载点数失败:', error)
        this.totalPoints = '0'
      }
    },

    async fetchHotData() {
      await Promise.all([
        this.fetchDouyin(),
        this.fetchWeibo(),
        this.fetchZhihu(),
        this.fetchBaidu()
      ])
    },

    async fetchDouyin() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=douyin')
        const data = await response.json()
        if (data.code === 200 && data.data) {
          this.douyinData = data.data
        } else {
          this.douyinData = []
        }
      } catch (error) {
        console.error('抖音数据获取失败:', error)
        this.douyinData = []
      }
    },

    async fetchWeibo() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=weibo')
        const data = await response.json()
        if (data.code === 200 && data.data) {
          this.weiboData = data.data
        } else {
          this.weiboData = []
        }
      } catch (error) {
        console.error('微博数据获取失败:', error)
        this.weiboData = []
      }
    },

    async fetchZhihu() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=qqnews')
        const data = await response.json()
        if (data.code === 200 && data.data) {
          this.zhihuData = data.data
        } else {
          this.zhihuData = []
        }
      } catch (error) {
        console.error('腾讯新闻数据获取失败:', error)
        this.zhihuData = []
      }
    },

    async fetchBaidu() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=baidu')
        const data = await response.json()
        if (data.code === 200 && data.data) {
          this.baiduData = data.data
        } else {
          this.baiduData = []
        }
      } catch (error) {
        console.error('百度数据获取失败:', error)
        this.baiduData = []
      }
    },

    // 生成默认的模拟数据（最近7天）
    generateDefaultChartData() {
      const dates = []
      const counts = []
      const today = new Date()
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(today.getDate() - i)
        dates.push(`${date.getMonth() + 1}/${date.getDate()}`)
        counts.push(Math.floor(Math.random() * 50) + 10)
      }
      return dates.map((date, index) => ({ date, count: counts[index] }))
    },

    async fetchChartData() {
      if (!this.userInfo || !this.userInfo.id) {
        // 没有用户信息时使用默认数据
        this.dailyGenerated = this.generateDefaultChartData()
        this.dailyPublished = this.generateDefaultChartData()
        this.updateCharts()
        return
      }

      try {
        // 获取生成视频数据
        const generatedResponse = await axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
          store_id: this.userInfo.id,
          action: 6
        })

        // 判断返回数据是否为空
        if (generatedResponse.data &&
          generatedResponse.data.daily_generated &&
          generatedResponse.data.daily_generated.length > 0) {
          this.dailyGenerated = generatedResponse.data.daily_generated
        } else {
          this.dailyGenerated = this.generateDefaultChartData()
        }

        // 获取发布视频数据
        const publishedResponse = await axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
          store_id: this.userInfo.id,
          action: 7
        })

        // 判断返回数据是否为空
        if (publishedResponse.data &&
          publishedResponse.data.daily_published &&
          publishedResponse.data.daily_published.length > 0) {
          this.dailyPublished = publishedResponse.data.daily_published
        } else {
          this.dailyPublished = this.generateDefaultChartData()
        }

        this.updateCharts()
      } catch (error) {
        console.error('获取图表数据失败:', error)
        // 接口请求失败时使用默认数据
        this.dailyGenerated = this.generateDefaultChartData()
        this.dailyPublished = this.generateDefaultChartData()
        this.updateCharts()
      }
    },

    initCharts() {
      const generateChartDom = document.getElementById('generateChart')
      const publishChartDom = document.getElementById('publishChart')

      if (generateChartDom) {
        this.genChart = echarts.init(generateChartDom)
      }
      if (publishChartDom) {
        this.pubChart = echarts.init(publishChartDom)
      }

      this.updateCharts()
      window.addEventListener('resize', this.handleResize)
    },

    updateCharts() {
      // 确保有数据
      const generateData = this.dailyGenerated.length > 0 ? this.dailyGenerated : this.generateDefaultChartData()
      const publishData = this.dailyPublished.length > 0 ? this.dailyPublished : this.generateDefaultChartData()

      const generateOption = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(18, 14, 33, 0.95)',
          borderColor: '#9d4edd',
          borderWidth: 1,
          textStyle: { color: '#fff' }
        },
        grid: { top: 30, left: 50, right: 20, bottom: 30, containLabel: true },
        xAxis: {
          type: 'category',
          data: generateData.map(item => item.date),
          axisLabel: { rotate: 30, color: '#a0a0c0' },
          axisLine: { lineStyle: { color: 'rgba(157, 78, 221, 0.3)' } }
        },
        yAxis: {
          type: 'value',
          name: '数量',
          nameTextStyle: { color: '#a0a0c0' },
          axisLabel: { color: '#a0a0c0' },
          splitLine: { lineStyle: { color: 'rgba(157, 78, 221, 0.1)' } }
        },
        series: [{
          name: '生成视频',
          type: 'bar',
          data: generateData.map(item => item.count),
          itemStyle: {
            borderRadius: [8, 8, 0, 0],
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#9d4edd' },
                { offset: 1, color: '#6546b4' }
              ]
            }
          },
          label: { show: true, position: 'top', color: '#c084fc' }
        }]
      }

      const publishOption = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(18, 14, 33, 0.95)',
          borderColor: '#43e97b',
          borderWidth: 1,
          textStyle: { color: '#fff' }
        },
        grid: { top: 30, left: 50, right: 20, bottom: 30, containLabel: true },
        xAxis: {
          type: 'category',
          data: publishData.map(item => item.date),
          axisLabel: { rotate: 30, color: '#a0a0c0' },
          axisLine: { lineStyle: { color: 'rgba(157, 78, 221, 0.3)' } }
        },
        yAxis: {
          type: 'value',
          name: '数量',
          nameTextStyle: { color: '#a0a0c0' },
          axisLabel: { color: '#a0a0c0' },
          splitLine: { lineStyle: { color: 'rgba(157, 78, 221, 0.1)' } }
        },
        series: [{
          name: '发布视频',
          type: 'line',
          data: publishData.map(item => item.count),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 3, color: '#43e97b' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(67, 233, 123, 0.3)' },
                { offset: 1, color: 'rgba(67, 233, 123, 0)' }
              ]
            }
          },
          label: { show: true, position: 'top', color: '#43e97b' },
          itemStyle: { color: '#43e97b', borderColor: '#fff', borderWidth: 2 }
        }]
      }

      if (this.genChart) {
        this.genChart.setOption(generateOption, true)
      }
      if (this.pubChart) {
        this.pubChart.setOption(publishOption, true)
      }
    },

    handleResize() {
      if (this.genChart) {
        this.genChart.resize()
      }
      if (this.pubChart) {
        this.pubChart.resize()
      }
    },

    formatHot(hot) {
      if (!hot) return ''
      if (hot >= 10000) {
        return (hot / 10000).toFixed(1) + 'w'
      }
      return hot.toString()
    },

    openLink(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },

    async fetchDashboardStats() {
      if (!this.userInfo || !this.userInfo.id) {
        this.stats.forEach(stat => {
          stat.value = '--'
          stat.loading = false
        })
        return
      }

      const uniqueActions = [...new Set(this.stats.map(stat => stat.action))]
      const actionCards = {}

      this.stats.forEach((stat, index) => {
        if (!actionCards[stat.action]) {
          actionCards[stat.action] = []
        }
        actionCards[stat.action].push({ key: stat.key, index })
      })

      try {
        const requests = uniqueActions.map(action =>
          axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
            store_id: this.userInfo.id,
            action: action
          })
        )

        const responses = await Promise.all(requests)

        responses.forEach((response, idx) => {
          const data = response.data
          const action = uniqueActions[idx]

          if (!data.error) {
            actionCards[action].forEach(cardInfo => {
              const stat = this.stats[cardInfo.index]
              stat.loading = false

              if (action === 3) {
                if (cardInfo.key === 'total_published') {
                  stat.value = data.total_published || 0
                } else if (cardInfo.key === 'today_published') {
                  stat.value = data.today_published || 0
                }
              } else {
                const key = stat.key
                stat.value = data[key] || 0
              }
            })
          } else {
            actionCards[action].forEach(cardInfo => {
              this.stats[cardInfo.index].value = '--'
              this.stats[cardInfo.index].loading = false
            })
          }
        })
      } catch (error) {
        console.error('获取统计数据失败:', error)
        this.stats.forEach(stat => {
          stat.value = '--'
          stat.loading = false
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* 欢迎横幅 */
.welcome-banner {
  background: linear-gradient(135deg, rgba(101, 70, 180, 0.4), rgba(157, 78, 221, 0.25));
  border-radius: 24px;
  padding: 32px 40px;
  margin-bottom: 32px;
  border: 1px solid rgba(157, 78, 221, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(157, 78, 221, 0.2), transparent);
    border-radius: 50%;
    pointer-events: none;
  }

  .banner-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
    position: relative;
    z-index: 1;

    .welcome-text {
      h1 {
        font-size: 28px;
        font-weight: 700;
        background: linear-gradient(135deg, #fff, #c084fc);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        margin-bottom: 8px;
      }

      p {
        color: #a0a0c0;
        font-size: 14px;
      }
    }

    .banner-stats {
      .banner-stat-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 24px;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 16px;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(157, 78, 221, 0.3);

        svg {
          color: #fff !important;
          /* 添加这行，强制图标为白色 */
        }

        .stat-num {
          font-size: 28px;
          font-weight: 700;
          color: #c084fc;
          margin-right: 8px;
        }

        .stat-label {
          font-size: 12px;
          color: #a0a0c0;
        }
      }
    }
  }
}

/* 统计卡片网格 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-card {
    background: rgba(18, 14, 33, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid rgba(157, 78, 221, 0.2);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      border-color: rgba(157, 78, 221, 0.5);
      box-shadow: 0 10px 30px rgba(101, 70, 180, 0.2);
    }

    .card-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }

    .card-info {
      flex: 1;

      .card-value {
        font-size: 28px;
        font-weight: 700;
        color: #fff;
        line-height: 1.2;
        margin-bottom: 4px;
      }

      .card-label {
        font-size: 13px;
        color: #a0a0c0;
      }
    }
  }
}

/* 快捷功能 */
.quick-actions {
  margin-bottom: 32px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .actions-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    .action-card {
      background: rgba(18, 14, 33, 0.6);
      backdrop-filter: blur(10px);
      border-radius: 16px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
      border: 1px solid rgba(157, 78, 221, 0.2);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        border-color: rgba(157, 78, 221, 0.5);
        background: rgba(101, 70, 180, 0.2);
      }

      .action-icon {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 12px;
        color: #fff;
      }

      .action-name {
        font-size: 14px;
        font-weight: 500;
        color: #fff;
      }
    }
  }
}

/* 图表区域 */
.chart-section {
  margin-bottom: 32px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .chart-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .chart-card {
      background: rgba(18, 14, 33, 0.6);
      backdrop-filter: blur(10px);
      border-radius: 20px;
      border: 1px solid rgba(157, 78, 221, 0.2);
      overflow: hidden;

      .chart-header {
        padding: 16px 20px;
        border-bottom: 1px solid rgba(157, 78, 221, 0.2);
        display: flex;
        align-items: center;
        gap: 8px;

        .chart-icon {
          &.generate {
            color: #9d4edd;
          }

          &.publish {
            color: #43e97b;
          }
        }

        span {
          font-size: 14px;
          font-weight: 500;
          color: #fff;
        }
      }
    }
  }
}

/* 热点榜单 */
.hotspot-section {
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hotspot-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }

    .hotspot-card {
      background: rgba(18, 14, 33, 0.6);
      backdrop-filter: blur(10px);
      border-radius: 20px;
      border: 1px solid rgba(157, 78, 221, 0.2);
      overflow: hidden;

      .hotspot-header {
        padding: 14px 16px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #fff;
        font-weight: 600;

        span {
          font-size: 15px;
        }
      }

      .hotspot-list {
        padding: 8px;
        max-height: 400px;
        overflow-y: auto;

        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(157, 78, 221, 0.1);
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(157, 78, 221, 0.4);
          border-radius: 4px;
        }

        .empty-data {
          text-align: center;
          padding: 40px 20px;
          color: #7a7a9a;
          font-size: 14px;
        }

        .hotspot-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;

          &:hover {
            background: rgba(157, 78, 221, 0.15);
          }

          .hotspot-rank {
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 14px;
            flex-shrink: 0;

            /* 默认样式（第4名及以后） */
            background: rgba(157, 78, 221, 0.3);
            color: #c084fc;

            &.rank-1 {
              background: linear-gradient(135deg, #fe2c55, #ff6b6b);
              border-radius: 8px;
              color: #fff;
            }

            &.rank-2 {
              background: linear-gradient(135deg, #ff9500, #ffb347);
              border-radius: 8px;
              color: #fff;
            }

            &.rank-3 {
              background: linear-gradient(135deg, #ffcc00, #ffdb4d);
              border-radius: 8px;
              color: #fff;
            }
          }

          .hotspot-text {
            flex: 1;
            font-size: 13px;
            color: #e0e0e0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .hotspot-hot,
          .hotspot-author {
            font-size: 11px;
            color: #7a7a9a;
            flex-shrink: 0;
          }
        }
      }
    }
  }
}
</style>